import React from 'react';
import { X, GripVertical } from 'lucide-react';
import {
  DndContext,
  PointerSensor,
  KeyboardSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  rectSortingStrategy,
  arrayMove,
  useSortable,
  sortableKeyboardCoordinates,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

/**
 * Sortable gallery grid for the partner edit modal.
 *
 * Drag any thumbnail to reorder; the first image is the partner's
 * "hero" everywhere it's rendered. Built on @dnd-kit/sortable so it's
 * keyboard-accessible (Tab → Space → arrow keys → Space).
 */

const SortableThumb = ({ id, src, idx, onRemove }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 50 : 'auto',
    opacity: isDragging ? 0.85 : 1,
  };
  return (
    <div
      ref={setNodeRef}
      style={style}
      className="relative group rounded-lg border border-border bg-card overflow-hidden"
      data-testid={`gallery-sortable-${idx}`}
    >
      <img src={src} alt={`Gallery ${idx + 1}`} className="w-full h-24 object-cover" draggable={false} />

      {/* Drag handle — covers most of the thumb so it's easy to grab,
         but skips the remove button corner. */}
      <button
        type="button"
        {...attributes}
        {...listeners}
        className="absolute inset-0 cursor-grab active:cursor-grabbing focus:outline-none focus:ring-2 focus:ring-primary"
        aria-label={`Drag to reorder image ${idx + 1}`}
        data-testid={`gallery-drag-handle-${idx}`}
      >
        <span className="sr-only">Drag to reorder</span>
      </button>

      {/* "Hero" badge on the first image so admins know what they're picking */}
      {idx === 0 && (
        <span className="absolute top-1 left-1 px-1.5 py-0.5 rounded text-[10px] font-medium bg-primary text-primary-foreground pointer-events-none">
          Hero
        </span>
      )}

      {/* Grip icon hint */}
      <div className="absolute bottom-1 left-1 p-1 rounded bg-black/40 text-white opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
        <GripVertical className="h-3 w-3" />
      </div>

      {/* Remove (must be above the drag overlay) */}
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onRemove(idx);
        }}
        className="absolute top-1 right-1 z-10 bg-destructive text-destructive-foreground p-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
        data-testid={`gallery-remove-${idx}`}
        aria-label={`Remove image ${idx + 1}`}
      >
        <X className="h-3 w-3" />
      </button>
    </div>
  );
};

const SortableGalleryGrid = ({ images, onRemove, onReorder }) => {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  if (!images || images.length === 0) return null;

  // Build stable ids that include the index so duplicate URLs (which can
  // happen if a file was uploaded twice) still get unique drag ids.
  const items = images.map((src, idx) => `${idx}::${src}`);

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = items.indexOf(active.id);
    const newIndex = items.indexOf(over.id);
    if (oldIndex < 0 || newIndex < 0) return;
    onReorder(arrayMove(images, oldIndex, newIndex));
  };

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
      <SortableContext items={items} strategy={rectSortingStrategy}>
        <div className="grid grid-cols-3 gap-2" data-testid="gallery-sortable-grid">
          {images.map((src, idx) => (
            <SortableThumb key={items[idx]} id={items[idx]} src={src} idx={idx} onRemove={onRemove} />
          ))}
        </div>
      </SortableContext>
    </DndContext>
  );
};

export default SortableGalleryGrid;
